'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import CityAutocompleteInput from '@/components/common/CityAutocompleteInput';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthGuard } from '@/hooks/useAuthGuard';
import { useAuth } from '@/lib/auth';
import { MEETING_MANAGEMENT_ROLES } from '@/features/meeting-attendance/constants/roles';
import type { AllowedModeSource, CheckinMode } from '@/features/meeting-attendance/constants/checkin-mode';
import AllowedModeTag from '@/features/meeting-attendance/components/AllowedModeTag';
import { MeetingUserSelector, type MeetingUser } from '@/features/meeting-attendance/components/MeetingUserSelector';
import { meetingAttendanceApi } from '@/services/api/meeting-attendance';
import { formatRatio } from '@/utils/format';
import {
  COMMON_TIMEZONES,
  convertFromUTC,
  convertToUTC,
  formatInstantWithTimezone,
  getUserTimezone,
  TENANT_DEFAULT_TIMEZONE,
} from '@/features/meeting-attendance/utils/timezone';
import { formatMeetingDateTime } from '@/features/meeting-attendance/utils/date';
import { toast } from 'sonner';
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Calendar,
  ChevronDown,
  Clock,
  Edit,
  FileText,
  MapPin,
  QrCode,
  RefreshCw,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
  XCircle,
} from 'lucide-react';

interface RequiredAttendee {
  id: string;
  role: string;
  user?: MeetingUser;
  checkinMode?: CheckinMode | null;
  workCity?: string | null;
  allowedMode?: CheckinMode | null;
  allowedModeSource?: AllowedModeSource;
  defaultMode?: CheckinMode | null;
  isOverridden?: boolean;
}

interface MeetingAttendance {
  id: string;
  status: string;
  checkinTime: string | null;
  checkoutTime: string | null;
  isLate: boolean;
  isEarlyLeave: boolean;
  notes: string | null;
  checkinType: string | null;
  user: {
    id: string;
    displayName: string;
    email: string;
    department?: { id: string; name: string; code?: string | null } | null;
    position?: { id: string; name: string; level?: number | null } | null;
  };
}

interface Meeting {
  id: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  location: string | null;
  city?: string | null;
  enforceCheckinMode?: boolean;
  seriesId?: string | null;
  type: string;
  status: string;
  timezone: string;
  qrCodeOnline: string | null;
  qrCodeOffline: string | null;
  creator: {
    id: string;
    displayName: string;
    email: string;
  };
  attendances: MeetingAttendance[];
  requiredAttendees?: RequiredAttendee[];
  externalAttendees?: Array<{
    id: string;
    email: string;
    displayName: string | null;
    attendeeType: string | null;
    response: string | null;
    sourceRole: string;
  }>;
  outlookSync?: {
    bindingId: string;
    manageStatus: string;
    syncMode: 'AUTO' | 'LOCKED_BY_LOCAL_EDIT';
    localOverrideAt?: string | null;
    localOverrideByUserId?: string | null;
    localOverrideByEmail?: string | null;
    localOverrideReason?: string | null;
    localOverrideFields?: string[] | null;
    primaryMailbox?: {
      id: string;
      mailboxEmail: string;
    } | null;
  } | null;
}

const ATTENDEE_ROLE_LABELS: Record<string, (t: any) => string> = {
  C_SUITE_ATTENDEE: (t) => t.meetingAttendance.attendees.roleCsuite,
  LEVEL_1_REPORT_ATTENDEE: (t) => t.meetingAttendance.attendees.roleLevel1,
  LEVEL_2_REPORT_ATTENDEE: (t) => t.meetingAttendance.attendees.roleLevel2,
  MEETING_OPERATIONS: (t) => t.meetingAttendance.attendees.roleOperations,
  REGULAR_ATTENDEE: (t) => t.meetingAttendance.attendees.roleRegular,
  OPTIONAL_ATTENDEE: (t) => t.meetingAttendance.attendees.roleOptional,
};

export default function MeetingAttendanceMeetingDetailPage() {
  const params = useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const { isReady } = useAuthGuard();
  const { hasAnyRole, isAdmin } = useAuth();
  const meetingId = params.id as string;
  const checkinMethodLabels = useMemo(() => ({
    QR_CODE: t.meetingAttendance.meetingDetail.checkinMethodQr,
    MANUAL: t.meetingAttendance.meetingDetail.checkinMethodManual,
    OTHER: t.meetingAttendance.meetingDetail.checkinMethodOther,
  }), [t]);

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddAttendees, setShowAddAttendees] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<MeetingUser[]>([]);
  const [requiredAttendees, setRequiredAttendees] = useState<RequiredAttendee[]>([]);
  const [selectedRole, setSelectedRole] = useState('OPTIONAL_ATTENDEE');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchFilter, setSearchFilter] = useState('');
  const [allowedFilter, setAllowedFilter] = useState<'ALL' | 'ON_SITE' | 'ONLINE' | 'NOT_SET' | 'OVERRIDDEN'>('ALL');
  const [togglingEnforce, setTogglingEnforce] = useState(false);
  const [tagMenuFor, setTagMenuFor] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [resumingSync, setResumingSync] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    city: '',
    enforceCheckinMode: false,
    type: 'OFFLINE',
    timezone: TENANT_DEFAULT_TIMEZONE,
  });
  const displayTimezone = useMemo(() => getUserTimezone(), []);

  const canManage = isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES);

  const fetchMeeting = useCallback(async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const data = await meetingAttendanceApi.getMeeting(meetingId);
      setMeeting(data);
    } catch (error) {
      console.error('Failed to fetch meeting details:', error);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [meetingId]);

  const fetchRequiredAttendees = useCallback(async () => {
    try {
      const data = await meetingAttendanceApi.listRequiredAttendees(meetingId);
      setRequiredAttendees(data.requiredAttendees || []);
    } catch (error) {
      console.error('Failed to fetch required attendees:', error);
    }
  }, [meetingId]);

  useEffect(() => {
    if (!isReady) {
      return;
    }
    fetchMeeting();
    fetchRequiredAttendees();
  }, [fetchMeeting, fetchRequiredAttendees, isReady]);

  useEffect(() => {
    if (!tagMenuFor) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-column="allowedMode"]')) return;
      setTagMenuFor(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [tagMenuFor]);

  useEffect(() => {
    if (!meetingId) {
      return;
    }
    const refreshInterval = setInterval(() => {
      fetchMeeting(true);
      fetchRequiredAttendees();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [fetchMeeting, fetchRequiredAttendees, meetingId]);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchMeeting(true), fetchRequiredAttendees()]);
    setRefreshing(false);
  };

  const handleDeleteMeeting = async () => {
    if (!window.confirm(t.meetingAttendance.messages.deleteConfirm)) {
      return;
    }

    try {
      const result = await meetingAttendanceApi.deleteMeeting(meetingId);
      toast.success(result.message || t.meetingAttendance.messages.updateSuccess);
      if (result.type === 'hard_delete') {
        router.push('/meetingattendance/meetings');
      } else {
        fetchMeeting();
      }
    } catch (error) {
      console.error('Failed to delete meeting:', error);
      toast.error(t.meetingAttendance.messages.deleteFailed);
    }
  };

  const startEditMeeting = () => {
    if (!meeting) {
      return;
    }
    const meetingTimezone = meeting.timezone || TENANT_DEFAULT_TIMEZONE;
    setEditFormData({
      title: meeting.title,
      description: meeting.description || '',
      startTime: convertFromUTC(new Date(meeting.startTime), meetingTimezone),
      endTime: convertFromUTC(new Date(meeting.endTime), meetingTimezone),
      location: meeting.location || '',
      city: meeting.city || '',
      enforceCheckinMode: meeting.enforceCheckinMode ?? false,
      type: meeting.type,
      timezone: meetingTimezone,
    });
    setShowEditForm(true);
  };

  const handleEditMeeting = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const submitData = {
        title: editFormData.title,
        description: editFormData.description,
        startTime: convertToUTC(editFormData.startTime, editFormData.timezone).toISOString(),
        endTime: convertToUTC(editFormData.endTime, editFormData.timezone).toISOString(),
        location: editFormData.location,
        city: editFormData.city || null,
        type: editFormData.type,
        timezone: editFormData.timezone,
      };

      await meetingAttendanceApi.updateMeeting(meetingId, submitData);

      if ((editFormData.enforceCheckinMode ?? false) !== (meeting?.enforceCheckinMode ?? false)) {
        try {
          await meetingAttendanceApi.updateMeetingEnforceCheckinMode(meetingId, editFormData.enforceCheckinMode);
        } catch (err) {
          console.error('Failed to toggle enforce checkin mode on save:', err);
          toast.error(t.meetingAttendance.enforceCheckinMode.toggleFailed);
        }
      }
      toast.success(t.meetingAttendance.messages.updateSuccess);
      setShowEditForm(false);
      fetchMeeting();
    } catch (error) {
      console.error('Failed to update meeting:', error);
      toast.error(t.meetingAttendance.messages.updateFailed);
    }
  };

  const updateAttendanceStatus = async (userId: string, status: string, notes?: string) => {
    try {
      await meetingAttendanceApi.updateAttendanceStatus(meetingId, userId, { status, notes });
      fetchMeeting();
    } catch (error) {
      console.error('Failed to update attendance status:', error);
      toast.error(t.meetingAttendance.messages.updateFailed);
    }
  };

  const addRequiredAttendees = async () => {
    if (selectedUsers.length === 0) {
      return;
    }

    try {
      await meetingAttendanceApi.addRequiredAttendees(meetingId, {
        attendees: selectedUsers.map((user) => ({
          userId: user.id,
          role: selectedRole,
        })),
        markAsCustom: true,
      });

      toast.success(t.meetingAttendance.messages.attendeesAdded);
      setSelectedUsers([]);
      fetchRequiredAttendees();
      fetchMeeting();
    } catch (error) {
      console.error('Failed to add required attendees:', error);
      toast.error(getErrorMessage(error, t.meetingAttendance.messages.updateFailed));
    }
  };

  const handleResumeOutlookSync = async () => {
    const bindingId = meeting?.outlookSync?.bindingId;
    if (!bindingId) return;
    if (!window.confirm(t.meetingAttendance.meetingDetail.syncResumeConfirm)) return;
    try {
      setResumingSync(true);
      await meetingAttendanceApi.resumeOutlookBindingSync(bindingId);
      toast.success(t.meetingAttendance.meetingDetail.syncResumeSuccess);
      fetchMeeting(true);
    } catch (error) {
      console.error('Failed to resume outlook sync:', error);
      toast.error(getErrorMessage(error, t.meetingAttendance.meetingDetail.syncResumeFailed));
    } finally {
      setResumingSync(false);
    }
  };

  const handleToggleEnforceCheckinMode = async (next: boolean) => {
    if (!meeting) return;
    if (next && !meeting.city?.trim()) {
      toast.error(t.meetingAttendance.enforceCheckinMode.guardTip);
      return;
    }
    try {
      setTogglingEnforce(true);
      await meetingAttendanceApi.updateMeetingEnforceCheckinMode(meetingId, next);
      toast.success(t.meetingAttendance.enforceCheckinMode.toggleSuccess);
      fetchMeeting(true);
      fetchRequiredAttendees();
    } catch (error) {
      console.error('Failed to toggle enforce checkin mode:', error);
      toast.error(
        getErrorMessage(error, t.meetingAttendance.enforceCheckinMode.toggleFailed),
      );
    } finally {
      setTogglingEnforce(false);
    }
  };

  const handleUpdateAttendeeCheckinMode = async (
    userId: string,
    mode: CheckinMode | null,
  ) => {
    try {
      await meetingAttendanceApi.updateRequiredAttendeeCheckinMode(meetingId, userId, mode);
      toast.success(t.meetingAttendance.allowedMode.actionUpdateSuccess);
      setTagMenuFor(null);
      fetchRequiredAttendees();
    } catch (error) {
      console.error('Failed to update attendee checkin mode:', error);
      toast.error(getErrorMessage(error, t.meetingAttendance.allowedMode.actionUpdateFailed));
    }
  };

  const removeRequiredAttendee = async (userId: string) => {
    if (!window.confirm(t.meetingAttendance.messages.removeAttendeeConfirm)) {
      return;
    }

    try {
      await meetingAttendanceApi.removeRequiredAttendee(meetingId, userId);
      toast.success(t.meetingAttendance.messages.attendeeRemoved);
      fetchRequiredAttendees();
      fetchMeeting();
    } catch (error) {
      console.error('Failed to remove required attendee:', error);
      toast.error(getErrorMessage(error, t.meetingAttendance.messages.updateFailed));
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      ON_SITE: { label: t.meetingAttendance.attendance.onSite, color: 'bg-emerald-100 text-emerald-800' },
      ONLINE: { label: t.meetingAttendance.attendance.online, color: 'bg-cyan-100 text-cyan-800' },
      ABSENT: { label: t.meetingAttendance.attendance.absent, color: 'bg-red-100 text-red-800' },
      PTO: { label: t.meetingAttendance.attendance.pto, color: 'bg-pink-100 text-pink-800' },
      BUSINESS_CONFLICT: { label: t.meetingAttendance.attendance.businessConflict, color: 'bg-amber-100 text-amber-800' },
      LATE: { label: t.meetingAttendance.attendance.late, color: 'bg-yellow-100 text-yellow-800' },
      NOT_CHECKED_IN: { label: t.meetingAttendance.attendance.notCheckedIn, color: 'bg-gray-100 text-gray-600' },
    };

    const statusInfo = statusMap[status] || statusMap.NOT_CHECKED_IN;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getCheckinMethodLabel = useCallback((method?: string | null) => {
    if (!method) return '';
    if (method === 'QR_CODE') return checkinMethodLabels.QR_CODE;
    if (method === 'MANUAL' || method === 'ATTENDANCE_MANUAL') return checkinMethodLabels.MANUAL;
    return checkinMethodLabels.OTHER;
  }, [checkinMethodLabels]);

  const getErrorMessage = useCallback((error: unknown, fallback: string) => {
    const message = (error as any)?.response?.data?.error || (error as any)?.message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    return fallback;
  }, []);

  const getMeetingStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; color: string }> = {
      SCHEDULED: { label: t.meetingAttendance.status.scheduled, color: 'bg-blue-100 text-blue-800' },
      IN_PROGRESS: { label: t.meetingAttendance.status.inProgress, color: 'bg-green-100 text-green-800' },
      COMPLETED: { label: t.meetingAttendance.status.completed, color: 'bg-gray-100 text-gray-800' },
      CANCELLED: { label: t.meetingAttendance.status.cancelled, color: 'bg-red-100 text-red-800' },
    };
    const statusInfo = statusMap[status] || statusMap.SCHEDULED;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      OFFLINE: { label: t.meetingAttendance.type.offline, color: 'bg-purple-100 text-purple-800' },
      ONLINE: { label: t.meetingAttendance.type.online, color: 'bg-green-100 text-green-800' },
      HYBRID: { label: t.meetingAttendance.type.hybrid, color: 'bg-yellow-100 text-yellow-800' },
    };
    const typeInfo = typeMap[type] || typeMap.OFFLINE;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
        {typeInfo.label}
      </span>
    );
  };

  const allAttendeeData = useMemo(() => {
    if (!meeting) {
      return [];
    }

    const attendeeMap = new Map<string, any>();

    meeting.attendances.forEach((attendance) => {
      const requiredAttendee = requiredAttendees.find((req) => req.user?.id === attendance.user.id);
      attendeeMap.set(attendance.user.id, {
        id: attendance.user.id,
        displayName: attendance.user.displayName,
        email: attendance.user.email,
        department: attendance.user.department?.name || '',
        position: attendance.user.position?.name || '',
        role: requiredAttendee?.role || '',
        status: attendance.status,
        checkinTime: attendance.checkinTime,
        checkinType: attendance.checkinType,
        notes: attendance.notes,
        hasAttendance: true,
        allowedMode: requiredAttendee?.allowedMode ?? null,
        allowedModeSource: requiredAttendee?.allowedModeSource,
        defaultMode: requiredAttendee?.defaultMode ?? null,
        isOverridden: requiredAttendee?.isOverridden ?? false,
        workCity: requiredAttendee?.workCity ?? null,
      });
    });

    requiredAttendees.forEach((required) => {
      if (required.user && !attendeeMap.has(required.user.id)) {
        attendeeMap.set(required.user.id, {
          id: required.user.id,
          displayName: required.user.displayName,
          email: required.user.email,
          department: required.user.department?.name || '',
          position: required.user.position?.name || '',
          role: required.role,
          status: 'NOT_CHECKED_IN',
          checkinTime: null,
          checkinType: null,
          notes: null,
          hasAttendance: false,
          allowedMode: required.allowedMode ?? null,
          allowedModeSource: required.allowedModeSource,
          defaultMode: required.defaultMode ?? null,
          isOverridden: required.isOverridden ?? false,
          workCity: required.workCity ?? null,
        });
      }
    });

    return Array.from(attendeeMap.values());
  }, [meeting, requiredAttendees]);

  const filteredAttendees = allAttendeeData.filter((attendee) => {
    const statusMatch = statusFilter === 'ALL'
      ? true
      : statusFilter === 'OPTIONAL_ONLY'
      ? attendee.role === 'OPTIONAL_ATTENDEE'
      : statusFilter === 'NOT_CHECKED_IN'
      ? attendee.status === 'NOT_CHECKED_IN' && attendee.role !== 'OPTIONAL_ATTENDEE'
      : attendee.status === statusFilter && attendee.role !== 'OPTIONAL_ATTENDEE';

    const searchMatch = searchFilter === ''
      ? true
      : attendee.displayName.toLowerCase().includes(searchFilter.toLowerCase())
      || attendee.email.toLowerCase().includes(searchFilter.toLowerCase())
      || (attendee.department && attendee.department.toLowerCase().includes(searchFilter.toLowerCase()))
      || (attendee.position && attendee.position.toLowerCase().includes(searchFilter.toLowerCase()));

    let allowedMatch = true;
    if (allowedFilter === 'ON_SITE') allowedMatch = attendee.allowedMode === 'ON_SITE';
    else if (allowedFilter === 'ONLINE') allowedMatch = attendee.allowedMode === 'ONLINE';
    else if (allowedFilter === 'NOT_SET') allowedMatch = attendee.allowedMode == null;
    else if (allowedFilter === 'OVERRIDDEN') allowedMatch = !!attendee.isOverridden;

    return statusMatch && searchMatch && allowedMatch;
  });

  const startText = meeting ? formatInstantWithTimezone(new Date(meeting.startTime), displayTimezone) : null;
  const endText = meeting ? formatInstantWithTimezone(new Date(meeting.endTime), displayTimezone) : null;

  if (!isReady || loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t.meetingAttendance.common.loading}</p>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t.meetingAttendance.meetingDetail.notFoundTitle}</h3>
            <p className="text-gray-500 text-center mb-4">
              {t.meetingAttendance.meetingDetail.notFoundDescription}
            </p>
            <Link href="/meetingattendance/meetings">
              <Button variant="outline">{t.meetingAttendance.common.back}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 应到/实到口径：仅统计 REGULAR_ATTENDEE，OPTIONAL_ATTENDEE 单独算
  const regularRequiredAttendees = requiredAttendees.filter((r) => r.role !== 'OPTIONAL_ATTENDEE');
  const optionalRequiredAttendees = requiredAttendees.filter((r) => r.role === 'OPTIONAL_ATTENDEE');
  const regularUserIds = new Set(regularRequiredAttendees.map((r) => r.user.id));
  const optionalUserIds = new Set(optionalRequiredAttendees.map((r) => r.user.id));
  const isAttendedStatus = (s: string) => s === 'ON_SITE' || s === 'ONLINE' || s === 'LATE';

  const totalRequiredAttendees = regularRequiredAttendees.length;
  const onSiteCount = meeting.attendances.filter((i) => i.status === 'ON_SITE' && regularUserIds.has(i.user.id)).length;
  const onlineCount = meeting.attendances.filter((i) => i.status === 'ONLINE' && regularUserIds.has(i.user.id)).length;
  const expectedOnSiteCount = regularRequiredAttendees.filter((req) => req.allowedMode === 'ON_SITE').length;
  const expectedOnlineCount = regularRequiredAttendees.filter((req) => req.allowedMode === 'ONLINE').length;
  const lateCount = meeting.attendances.filter((i) => i.status === 'LATE' && regularUserIds.has(i.user.id)).length;
  const absentCount = meeting.attendances.filter((i) => i.status === 'ABSENT' && regularUserIds.has(i.user.id)).length;
  const ptoCount = meeting.attendances.filter((i) => i.status === 'PTO' && regularUserIds.has(i.user.id)).length;
  const businessConflictCount = meeting.attendances.filter((i) => i.status === 'BUSINESS_CONFLICT' && regularUserIds.has(i.user.id)).length;
  const actualAttendees = onSiteCount + onlineCount + lateCount;
  const allStatusCount = onSiteCount + onlineCount + lateCount + absentCount + ptoCount + businessConflictCount;
  const notCheckedInCount = totalRequiredAttendees - allStatusCount;

  const optionalTotal = optionalRequiredAttendees.length;
  const optionalAttended = meeting.attendances.filter(
    (i) => optionalUserIds.has(i.user.id) && isAttendedStatus(i.status),
  ).length;
  const externalAttendees = meeting.externalAttendees || [];
  const formatExternalAttendeeType = (value?: string | null) => {
    if (!value) return '-';
    switch (value.toLowerCase()) {
      case 'required':
        return t.meetingAttendance.meetingDetail.externalAttendeeTypeRequired;
      case 'optional':
        return t.meetingAttendance.meetingDetail.externalAttendeeTypeOptional;
      case 'resource':
        return t.meetingAttendance.meetingDetail.externalAttendeeTypeResource;
      default:
        return value;
    }
  };

  const formatExternalResponse = (value?: string | null) => {
    if (!value) return '-';
    switch (value.toLowerCase()) {
      case 'accepted':
        return t.meetingAttendance.meetingDetail.externalResponseAccepted;
      case 'declined':
        return t.meetingAttendance.meetingDetail.externalResponseDeclined;
      case 'tentativelyaccepted':
        return t.meetingAttendance.meetingDetail.externalResponseTentative;
      case 'notresponded':
        return t.meetingAttendance.meetingDetail.externalResponseNotResponded;
      case 'none':
        return t.meetingAttendance.meetingDetail.externalResponseNone;
      default:
        return value;
    }
  };
  const getExternalRoleTagClassName = (sourceRole: string) => sourceRole === 'ORGANIZER'
    ? 'inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-200'
    : 'inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 border border-gray-200';
  const attendanceRate = totalRequiredAttendees > 0
    ? Math.round((actualAttendees / totalRequiredAttendees) * 100)
    : 0;
  const isLocallyManaged = meeting.outlookSync?.syncMode === 'LOCKED_BY_LOCAL_EDIT';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link href="/meetingattendance/meetings">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t.meetingAttendance.common.back}
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{meeting.title}</h1>
            <p className="mt-1 text-sm text-gray-500">{t.meetingAttendance.meetingDetail.subtitle}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getMeetingStatusBadge(meeting.status)}
          {getTypeBadge(meeting.type)}
        </div>
      </div>

      {isLocallyManaged ? (
        <Card className="border-amber-200 bg-amber-50/80">
          <CardContent className="flex items-start gap-3 py-4">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="space-y-1 text-sm text-amber-900 flex-1">
              <div className="font-medium">{t.meetingAttendance.meetingDetail.syncLockedTitle}</div>
              <div>{t.meetingAttendance.meetingDetail.syncLockedDescription}</div>
              <div className="text-xs text-amber-800/80">
                {t.meetingAttendance.meetingDetail.syncLockedMeta
                  .replace('{time}', meeting.outlookSync?.localOverrideAt ? formatMeetingDateTime(meeting.outlookSync.localOverrideAt) : '-')
                  .replace('{user}', meeting.outlookSync?.localOverrideByEmail || '-')
                  .replace('{reason}', meeting.outlookSync?.localOverrideReason || '-')}
              </div>
            </div>
            {canManage && meeting.outlookSync?.bindingId && (
              <Button
                variant="outline"
                size="sm"
                disabled={resumingSync}
                onClick={handleResumeOutlookSync}
                data-action="resume-outlook-sync"
                className="border-amber-300 text-amber-900 hover:bg-amber-100"
              >
                {resumingSync
                  ? t.meetingAttendance.meetingDetail.syncResumeLoading
                  : t.meetingAttendance.meetingDetail.syncResumeAction}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{t.meetingAttendance.meetingDetail.infoTitle}</span>
            <div className="flex space-x-2">
              <Link href={`/meetingattendance/${meetingId}/agenda`}>
                <Button size="sm" variant="outline">
                  <FileText className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.meetingDetail.viewAgenda}
                </Button>
              </Link>
              {canManage && (
                <Button onClick={startEditMeeting} size="sm" variant="outline">
                  <Edit className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.meetingDetail.editMeeting}
                </Button>
              )}
              {(meeting.qrCodeOnline || meeting.qrCodeOffline) && (
                <Link href={`/meetingattendance/meetings/${meetingId}/qr`}>
                  <Button variant="outline" size="sm">
                    <QrCode className="mr-2 h-4 w-4" />
                    {t.meetingAttendance.common.qrCodes}
                  </Button>
                </Link>
              )}
              <Link href={`/meetingattendance/reports?meetingId=${meetingId}`}>
                <Button variant="outline" size="sm">
                  <BarChart3 className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.meetingDetail.viewReports}
                </Button>
              </Link>
              {canManage && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteMeeting}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.meetingDetail.deleteMeeting}
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 flex items-center">
                <Clock className="mr-2 h-4 w-4" />
                {t.meetingAttendance.meetingDetail.scheduleLabel}
              </h4>
              <div className="text-sm text-gray-600 space-y-1">
                <div>{t.meetingAttendance.common.start}: {startText || '-'}</div>
                <div>{t.meetingAttendance.common.end}: {endText || '-'}</div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-gray-700">{t.meetingAttendance.meetingForm.descriptionLabel}</h4>
              <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">
                {meeting.description || t.meetingAttendance.meetingDetail.descriptionEmpty}
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 flex items-center">
                <MapPin className="mr-2 h-4 w-4" />
                {t.meetingAttendance.common.location}
              </h4>
              <p className="text-sm text-gray-600">
                {meeting.location || t.meetingAttendance.meetingDetail.locationEmpty}
              </p>
              <div className="pt-2 border-t border-gray-100 space-y-1">
                <div className="text-xs text-gray-500">{t.meetingAttendance.meetingDetail.cityLabel}</div>
                <div className="text-sm text-gray-700" data-field="meetingCity">
                  {meeting.city || t.meetingAttendance.meetingDetail.cityEmpty}
                </div>
              </div>
              <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-500">
                    {t.meetingAttendance.enforceCheckinMode.label}
                  </div>
                  <div className={`text-sm font-medium ${meeting.enforceCheckinMode ? 'text-emerald-600' : 'text-gray-500'}`} data-field="enforceCheckinMode">
                    {meeting.enforceCheckinMode
                      ? t.meetingAttendance.enforceCheckinMode.on
                      : t.meetingAttendance.enforceCheckinMode.off}
                  </div>
                </div>
                {canManage && (
                  <Switch
                    checked={meeting.enforceCheckinMode ?? false}
                    disabled={togglingEnforce || (!meeting.enforceCheckinMode && !meeting.city?.trim())}
                    onCheckedChange={(next) => handleToggleEnforceCheckinMode(next)}
                    data-action="toggle-enforce-checkin-mode"
                    aria-label={t.meetingAttendance.enforceCheckinMode.label}
                    title={!meeting.enforceCheckinMode && !meeting.city?.trim() ? t.meetingAttendance.enforceCheckinMode.guardTip : ''}
                  />
                )}
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-gray-700">{t.meetingAttendance.common.creator}</h4>
              <div className="text-sm">
                <p className="text-gray-900 font-medium">{meeting.creator.displayName}</p>
                <p className="text-gray-500">{meeting.creator.email}</p>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium text-gray-700 flex items-center">
                <Users className="mr-2 h-4 w-4" />
                {t.meetingAttendance.meetingDetail.attendanceRate}
              </h4>
              <div className="flex items-center space-x-2">
                <div className="text-3xl font-bold text-blue-600">{attendanceRate}%</div>
                <div className="text-sm text-gray-500">
                  ({actualAttendees}/{totalRequiredAttendees})
                </div>
              </div>
              <div className="text-xs text-gray-500">
                {t.meetingAttendance.meetingDetail.optionalLabel}: {formatRatio(optionalAttended, optionalTotal)}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-700 flex items-center">
                <Users className="mr-2 h-4 w-4" />
                {t.meetingAttendance.meetingDetail.attendanceStats}
                <span className="text-xs text-gray-500 ml-2 font-normal">
                  ({t.meetingAttendance.meetingDetail.attendanceStatsHint})
                </span>
              </h4>
              {statusFilter !== 'ALL' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setStatusFilter('ALL')}
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  {t.meetingAttendance.meetingDetail.clearFilter}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-8 gap-3">
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'ON_SITE' ? 'ALL' : 'ON_SITE')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'ON_SITE'
                    ? 'bg-emerald-100 ring-2 ring-emerald-500'
                    : 'bg-emerald-50'
                }`}
              >
                <div className="text-2xl font-bold text-emerald-600">
                  {onSiteCount}/{meeting.enforceCheckinMode ? expectedOnSiteCount : '-'}
                </div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.attendance.onSite}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'ONLINE' ? 'ALL' : 'ONLINE')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'ONLINE'
                    ? 'bg-emerald-100 ring-2 ring-emerald-500'
                    : 'bg-emerald-50'
                }`}
              >
                <div className="text-2xl font-bold text-emerald-600">
                  {onlineCount}/{meeting.enforceCheckinMode ? expectedOnlineCount : '-'}
                </div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.attendance.online}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'OPTIONAL_ONLY' ? 'ALL' : 'OPTIONAL_ONLY')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'OPTIONAL_ONLY'
                    ? 'bg-indigo-100 ring-2 ring-indigo-500'
                    : 'bg-indigo-50'
                }`}
                title={t.meetingAttendance.meetingDetail.optionalLabel}
              >
                <div className="text-2xl font-bold text-indigo-600">
                  {formatRatio(optionalAttended, optionalTotal)}
                </div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.meetingDetail.optionalLabel}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'LATE' ? 'ALL' : 'LATE')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'LATE'
                    ? 'bg-orange-200 ring-2 ring-orange-500'
                    : 'bg-orange-100'
                }`}
              >
                <div className="text-2xl font-bold text-orange-700">{lateCount}</div>
                <div className="text-xs text-orange-700 font-semibold">{t.meetingAttendance.attendance.late}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'ABSENT' ? 'ALL' : 'ABSENT')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'ABSENT'
                    ? 'bg-red-200 ring-2 ring-red-500'
                    : 'bg-red-100'
                }`}
              >
                <div className="text-2xl font-bold text-red-800">{absentCount}</div>
                <div className="text-xs text-red-800 font-semibold">{t.meetingAttendance.attendance.absent}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'PTO' ? 'ALL' : 'PTO')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'PTO'
                    ? 'bg-cyan-100 ring-2 ring-cyan-500'
                    : 'bg-cyan-50'
                }`}
              >
                <div className="text-2xl font-bold text-cyan-600">{ptoCount}</div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.attendance.pto}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'BUSINESS_CONFLICT' ? 'ALL' : 'BUSINESS_CONFLICT')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'BUSINESS_CONFLICT'
                    ? 'bg-cyan-100 ring-2 ring-cyan-500'
                    : 'bg-cyan-50'
                }`}
              >
                <div className="text-2xl font-bold text-cyan-600">{businessConflictCount}</div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.attendance.businessConflict}</div>
              </button>
              <button
                type="button"
                onClick={() => setStatusFilter(statusFilter === 'NOT_CHECKED_IN' ? 'ALL' : 'NOT_CHECKED_IN')}
                className={`text-center p-3 rounded-lg transition-all hover:shadow-md hover:scale-105 ${
                  statusFilter === 'NOT_CHECKED_IN'
                    ? 'bg-gray-200 ring-2 ring-gray-500'
                    : 'bg-gray-50'
                }`}
              >
                <div className="text-2xl font-bold text-gray-600">{notCheckedInCount}</div>
                <div className="text-xs text-gray-500">{t.meetingAttendance.attendance.notCheckedIn}</div>
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {canManage && (
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center justify-between mb-4">
              <span className="text-xl font-semibold">
                {t.meetingAttendance.meetingDetail.attendeeTitleWithCount
                  .replace('{current}', String(filteredAttendees.length))
                  .replace('{total}', String(allAttendeeData.length))}
              </span>
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <Input
                    type="text"
                    placeholder={t.meetingAttendance.meetingDetail.searchPlaceholder}
                    value={searchFilter}
                    onChange={(event) => setSearchFilter(event.target.value)}
                    className="pl-8 pr-3 py-2 w-48 text-sm"
                  />
                  <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
                <div className="relative">
                  <select
                    className="appearance-none bg-white border border-gray-300 rounded px-3 py-2 text-sm pr-8 cursor-pointer w-40"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    title={t.meetingAttendance.meetingDetail.filterTitle}
                  >
                    <option value="ALL">{t.meetingAttendance.meetingDetail.filterAll}</option>
                    <option value="ON_SITE">{t.meetingAttendance.attendance.onSite} ({onSiteCount})</option>
                    <option value="ONLINE">{t.meetingAttendance.attendance.online} ({onlineCount})</option>
                    <option value="ABSENT">{t.meetingAttendance.attendance.absent} ({absentCount})</option>
                    <option value="PTO">{t.meetingAttendance.attendance.pto} ({ptoCount})</option>
                    <option value="BUSINESS_CONFLICT">{t.meetingAttendance.attendance.businessConflict} ({businessConflictCount})</option>
                    <option value="LATE">{t.meetingAttendance.attendance.late} ({lateCount})</option>
                    <option value="NOT_CHECKED_IN">{t.meetingAttendance.attendance.notCheckedIn} ({notCheckedInCount})</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  title={t.meetingAttendance.meetingDetail.refresh}
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAddAttendees(true)}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t.meetingAttendance.meetingDetail.addAttendees}
                </Button>
              </div>
            </CardTitle>
            <CardDescription>{t.meetingAttendance.meetingDetail.attendeeSubtitle}</CardDescription>
          </CardHeader>
          <CardContent>
            {meeting.enforceCheckinMode && (
              <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="allowed-mode-chips">
                {([
                  ['ALL', t.meetingAttendance.meetingDetail.filterAll],
                  ['ON_SITE', t.meetingAttendance.allowedMode.filterOnSite],
                  ['ONLINE', t.meetingAttendance.allowedMode.filterOnline],
                  ['NOT_SET', t.meetingAttendance.allowedMode.filterNotSet],
                  ['OVERRIDDEN', t.meetingAttendance.allowedMode.filterOverridden],
                ] as const).map(([key, label]) => {
                  const active = allowedFilter === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setAllowedFilter(key as typeof allowedFilter)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                        active
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                      }`}
                      data-allowed-filter={key}
                      data-active={active}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            {showAddAttendees && (
              <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                <h4 className="font-medium mb-3">{t.meetingAttendance.meetingDetail.addAttendees}</h4>
                <div className="mb-4">
                  <Label htmlFor="role">{t.meetingAttendance.attendees.roleLabel}</Label>
                  <select
                    id="role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-1"
                    value={selectedRole}
                    onChange={(event) => setSelectedRole(event.target.value)}
                    title={t.meetingAttendance.attendees.roleLabel}
                  >
                    <option value="C_SUITE_ATTENDEE">{t.meetingAttendance.attendees.roleCsuite}</option>
                    <option value="LEVEL_1_REPORT_ATTENDEE">{t.meetingAttendance.attendees.roleLevel1}</option>
                    <option value="LEVEL_2_REPORT_ATTENDEE">{t.meetingAttendance.attendees.roleLevel2}</option>
                    <option value="MEETING_OPERATIONS">{t.meetingAttendance.attendees.roleOperations}</option>
                    <option value="REGULAR_ATTENDEE">{t.meetingAttendance.attendees.roleRegular}</option>
                    <option value="OPTIONAL_ATTENDEE">{t.meetingAttendance.attendees.roleOptional}</option>
                  </select>
                </div>
                <MeetingUserSelector
                  selectedUsers={selectedUsers}
                  onUsersChange={setSelectedUsers}
                />
                <div className="flex justify-end space-x-3 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAddAttendees(false);
                      setSelectedUsers([]);
                    }}
                  >
                    {t.meetingAttendance.attendees.cancel}
                  </Button>
                  <Button
                    size="sm"
                    onClick={addRequiredAttendees}
                    disabled={selectedUsers.length === 0}
                  >
                    {t.meetingAttendance.attendees.addConfirm} ({selectedUsers.length})
                  </Button>
                </div>
              </div>
            )}

            {filteredAttendees.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">
                  {statusFilter === 'ALL'
                    ? t.meetingAttendance.meetingDetail.noAttendees
                    : t.meetingAttendance.meetingDetail.noAttendeesWithStatus}
                </p>
              </div>
            ) : (
              <div className="relative">
                <div className="max-h-[600px] overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                  {filteredAttendees
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                    .map((attendee) => {
                      const attendance = meeting.attendances.find((item) => item.user.id === attendee.id);
                      return (
                        <div
                          key={attendee.id}
                          className={`flex items-center p-4 border rounded-lg hover:bg-gray-50 ${
                            attendee.hasAttendance ? 'border-gray-200' : 'border-orange-200 bg-orange-50'
                          }`}
                        >
                          <div className="flex items-center space-x-4 flex-1">
                            <div className="flex-shrink-0">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                attendee.hasAttendance ? 'bg-gray-200' : 'bg-orange-200'
                              }`}>
                                  <span className={`text-sm font-medium ${
                                    attendee.hasAttendance ? 'text-gray-600' : 'text-orange-600'
                                  }`}>
                                  {attendee.displayName.charAt(0)}
                                </span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{attendee.displayName}</h4>
                                {attendee.role && (
                                  <span className="text-xs text-blue-600 font-medium px-2 py-1 bg-blue-50 rounded">
                                    {(ATTENDEE_ROLE_LABELS[attendee.role] || ((tAny: any) => attendee.role))(t)}
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">
                                {attendee.department && <span>{attendee.department} · </span>}
                                {attendee.position}
                              </div>
                              <div className="text-sm text-gray-500">{attendee.email}</div>
                            </div>
                          </div>

                          {meeting.enforceCheckinMode && (
                            <div className="flex w-44 relative" data-column="allowedMode">
                              <button
                                type="button"
                                onClick={() =>
                                  canManage && setTagMenuFor(tagMenuFor === attendee.id ? null : attendee.id)
                                }
                                className={canManage ? 'cursor-pointer' : 'cursor-default'}
                                disabled={!canManage}
                                data-action="open-allowed-mode-menu"
                              >
                                <AllowedModeTag
                                  mode={attendee.allowedMode ?? null}
                                  source={attendee.allowedModeSource}
                                  defaultMode={attendee.defaultMode ?? null}
                                  showDataAttrs
                                />
                              </button>
                              {canManage && tagMenuFor === attendee.id && (
                                <div
                                  className="absolute top-8 z-30 bg-white border border-gray-200 rounded-md shadow-lg py-1 w-44"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50"
                                    onClick={() => handleUpdateAttendeeCheckinMode(attendee.id, 'ON_SITE')}
                                  >
                                    {t.meetingAttendance.allowedMode.actionChangeToOnSite}
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50"
                                    onClick={() => handleUpdateAttendeeCheckinMode(attendee.id, 'ONLINE')}
                                  >
                                    {t.meetingAttendance.allowedMode.actionChangeToOnline}
                                  </button>
                                  {attendee.isOverridden && (
                                    <button
                                      type="button"
                                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 text-gray-600"
                                      onClick={() => handleUpdateAttendeeCheckinMode(attendee.id, null)}
                                    >
                                      {t.meetingAttendance.allowedMode.actionRestoreDefault}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="flex items-center justify-center w-48">
                            {canManage ? (
                              <div className="relative">
                                <select
                                  className="appearance-none bg-white border border-gray-300 rounded px-3 py-1 text-xs pr-8 cursor-pointer w-40"
                                  value={attendance?.status || 'NOT_CHECKED_IN'}
                                  onChange={(event) => updateAttendanceStatus(attendee.id, event.target.value)}
                                  title={t.meetingAttendance.meetingDetail.selectAttendanceStatus}
                                >
                                  <option value="ON_SITE">{t.meetingAttendance.attendance.onSite}</option>
                                  <option value="ONLINE">{t.meetingAttendance.attendance.online}</option>
                                  <option value="ABSENT">{t.meetingAttendance.attendance.absent}</option>
                                  <option value="PTO">{t.meetingAttendance.attendance.pto}</option>
                                  <option value="BUSINESS_CONFLICT">{t.meetingAttendance.attendance.businessConflict}</option>
                                  <option value="LATE">{t.meetingAttendance.attendance.late}</option>
                                  <option value="NOT_CHECKED_IN">{t.meetingAttendance.attendance.notCheckedIn}</option>
                                </select>
                                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
                              </div>
                            ) : (
                              getStatusBadge(attendee.status)
                            )}
                          </div>

                          <div className="text-right w-48">
                            {attendee.checkinTime && (
                              <div className="text-xs text-gray-500">
                                {t.meetingAttendance.meetingDetail.checkinTime}: {formatMeetingDateTime(attendee.checkinTime)}
                              </div>
                            )}
                            {attendee.checkinType && (
                              <div className="text-xs text-gray-500">
                                {t.meetingAttendance.meetingDetail.checkinMethod}: {getCheckinMethodLabel(attendee.checkinType)}
                              </div>
                            )}
                            {attendee.notes && (
                              <div className="text-xs text-gray-500">
                                {t.meetingAttendance.meetingDetail.notes}: {attendee.notes}
                              </div>
                            )}
                            {canManage && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => removeRequiredAttendee(attendee.id)}
                                className="text-red-600 hover:text-red-700 mt-2"
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-xl font-semibold">
            {t.meetingAttendance.meetingDetail.externalAttendeesTitle}
          </CardTitle>
          <CardDescription>
            {t.meetingAttendance.meetingDetail.externalAttendeesSubtitle
              .replace('{count}', String(externalAttendees.length))}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {externalAttendees.length === 0 ? (
            <div className="text-sm text-gray-500">{t.meetingAttendance.meetingDetail.externalAttendeesEmpty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {externalAttendees.map((item) => (
                <div key={item.id} className="flex items-start gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      item.sourceRole === 'ORGANIZER' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <span className={`text-sm font-medium ${
                        item.sourceRole === 'ORGANIZER' ? 'text-blue-700' : 'text-gray-600'
                      }`}>
                        {(item.displayName || item.email).charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-gray-900 truncate">
                        {item.displayName || item.email.split('@')[0]}
                      </span>
                      <span className={getExternalRoleTagClassName(item.sourceRole)}>
                        {item.sourceRole === 'ORGANIZER'
                          ? t.meetingAttendance.meetingDetail.externalOrganizer
                          : t.meetingAttendance.meetingDetail.externalAttendee}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{item.email}</div>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-gray-500">
                      <span>{formatExternalAttendeeType(item.attendeeType)}</span>
                      <span className="text-gray-300">|</span>
                      <span>{formatExternalResponse(item.response)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {showEditForm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{t.meetingAttendance.meetingForm.editTitle}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEditForm(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form onSubmit={handleEditMeeting} className="space-y-4">
              <div>
                <Label htmlFor="title">{t.meetingAttendance.meetingForm.titleLabel}</Label>
                <Input
                  id="title"
                  value={editFormData.title}
                  onChange={(event) => setEditFormData({ ...editFormData, title: event.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">{t.meetingAttendance.meetingForm.descriptionLabel}</Label>
                <textarea
                  id="description"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={editFormData.description}
                  onChange={(event) => setEditFormData({ ...editFormData, description: event.target.value })}
                  placeholder={t.meetingAttendance.meetingForm.descriptionPlaceholder}
                />
              </div>

              <div>
                <Label htmlFor="timezone">{t.meetingAttendance.meetingForm.timezoneLabel}</Label>
                <select
                  id="timezone"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editFormData.timezone}
                  onChange={(event) => setEditFormData({ ...editFormData, timezone: event.target.value })}
                  title={t.meetingAttendance.meetingForm.timezoneLabel}
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startTime">{t.meetingAttendance.meetingForm.startTimeLabel}</Label>
                  <Input
                    id="startTime"
                    type="datetime-local"
                    value={editFormData.startTime}
                    onChange={(event) => setEditFormData({ ...editFormData, startTime: event.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="endTime">{t.meetingAttendance.meetingForm.endTimeLabel}</Label>
                  <Input
                    id="endTime"
                    type="datetime-local"
                    value={editFormData.endTime}
                    onChange={(event) => setEditFormData({ ...editFormData, endTime: event.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="location">{t.meetingAttendance.meetingForm.locationLabel}</Label>
                <Input
                  id="location"
                  value={editFormData.location}
                  onChange={(event) => setEditFormData({ ...editFormData, location: event.target.value })}
                  placeholder={t.meetingAttendance.meetingForm.locationPlaceholder}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-city">{t.meetingAttendance.meetingForm.cityLabel}</Label>
                  <CityAutocompleteInput
                    id="edit-city"
                    value={editFormData.city}
                    onChange={(v) => setEditFormData({ ...editFormData, city: v })}
                    placeholder={t.meetingAttendance.meetingForm.cityPlaceholder}
                  />
                  <p className="text-xs text-gray-500 mt-1">{t.meetingAttendance.meetingForm.cityHint}</p>
                </div>
                <div>
                  <Label htmlFor="edit-enforce-checkin">{t.meetingAttendance.enforceCheckinMode.label}</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      id="edit-enforce-checkin"
                      checked={editFormData.enforceCheckinMode}
                      disabled={!editFormData.enforceCheckinMode && !editFormData.city.trim()}
                      onCheckedChange={(next) => setEditFormData({ ...editFormData, enforceCheckinMode: next })}
                    />
                    <span className={`text-sm ${editFormData.enforceCheckinMode ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {editFormData.enforceCheckinMode
                        ? t.meetingAttendance.enforceCheckinMode.on
                        : t.meetingAttendance.enforceCheckinMode.off}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{t.meetingAttendance.meetingForm.enforceCheckinModeHint}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="type">{t.meetingAttendance.meetingForm.typeLabel}</Label>
                <select
                  id="type"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={editFormData.type}
                  onChange={(event) => setEditFormData({ ...editFormData, type: event.target.value })}
                  title={t.meetingAttendance.meetingForm.typeLabel}
                >
                  <option value="OFFLINE">{t.meetingAttendance.meetingForm.typeOffline}</option>
                  <option value="ONLINE">{t.meetingAttendance.meetingForm.typeOnline}</option>
                  <option value="HYBRID">{t.meetingAttendance.meetingForm.typeHybrid}</option>
                </select>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditForm(false)}
                >
                  {t.meetingAttendance.meetingForm.cancel}
                </Button>
                <Button type="submit">
                  {t.meetingAttendance.meetingForm.editAction}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
